import path from 'path'
import fsPromises from 'fs/promises'

import { type FuturesMarketAsset, PerpsProvider } from '@kwenta/sdk/types'
import { MarketKeyByAsset } from '@kwenta/sdk/utils'
import { DialogOverlay } from '@reach/dialog'
import { useRouter } from 'next/router'
import {
	type FC,
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import styled from 'styled-components'

import {
	CustomGridItemComponent,
	DRAG_HANDLE_CLASS_NAME,
	ResponsiveGridLayout,
} from 'components/GridLayout'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import { DEFAULT_PERPS_PROVIDER, SNX_V2_ENABLED, SNX_V3_BASE_ENABLED } from 'constants/futures'
import useClickOutside from 'hooks/useClickOutside'
import useWindowSize from 'hooks/useWindowSize'
import AbstractionAccountSetupModal from 'sections/accounts/AbstractionAccountSetupModal'
import DepositFundsModal from 'sections/accounts/DepositFundsModal'
import AcknowledgementModal from 'sections/app/AcknowledgementModal'
import ClosePositionModal from 'sections/futures/ClosePositionModal/ClosePositionModal'
import CrossMarginOnboarding from 'sections/futures/CrossMarginOnboarding'
import EditConditionalOrderModal from 'sections/futures/EditConditionalOrderModal/EditConditionalOrderModal'
import EditPositionMarginModal from 'sections/futures/EditPositionModal/EditPositionMarginModal'
import EditPositionSizeModal from 'sections/futures/EditPositionModal/EditPositionSizeModal'
import EditStopLossAndTakeProfitModal from 'sections/futures/EditPositionModal/EditStopLossAndTakeProfitModal'
import FundingChart from 'sections/futures/FundingChart'
import MarketDetails from 'sections/futures/MarketDetails'
import MarketHead from 'sections/futures/MarketInfo/MarketHead'
import MobileTrade from 'sections/futures/MobileTrade/MobileTrade'
import OneClickTradingOnboard from 'sections/futures/OneClickTradingOnboard/OneClickTradingOnboard'
import PositionChart from 'sections/futures/PositionChart'
import ManageCrossMarginModal from 'sections/futures/Trade/ManageCrossMarginModal'
import ManageIsolatedMarginModal from 'sections/futures/Trade/ManageIsolatedMarginModal'
import MarketsDropdownList from 'sections/futures/Trade/MarketsDropdownList'
import TradePanel from 'sections/futures/Trade/TradePanel'
import CrossMarginOrderConfirmationModal from 'sections/futures/TradeConfirmation/CrossMarginOrderConfirmation'
import TradeConfirmationModalSmartMargin from 'sections/futures/TradeConfirmation/TradeConfirmationModalSmartMargin'
import TableFiltersModal from 'sections/futures/UserInfo/TableFilters/TableFiltersModal'
import { TRADE_PANEL_WIDTH_MD } from 'sections/futures/styles'
import BaseReferralModal from 'sections/referrals/ReferralModal/BaseReferralModal'
import AppLayout from 'sections/shared/Layout/AppLayout'
import { setOpenModal } from 'state/app/reducer'
import {
	selectShowBanner,
	selectShowConditionalOrderModal,
	selectShowModal,
	selectShowPositionModal,
} from 'state/app/selectors'
import { clearTradeInputs, setMarketAsset } from 'state/futures/actions'
import {
	selectMarketAsset,
	selectOpenMarketDropdownList,
	selectPerpsProvider,
} from 'state/futures/common/selectors'
import type { MarketsDetailProps } from 'state/futures/common/types'
import { useFetchAbstractionData, usePollMarketFuturesData } from 'state/futures/hooks'
import { setMarketModules, setPreferences, setUserInfoTableFilter } from 'state/futures/reducer'
import {
	selectCustomLayout,
	selectDefaultLayouts,
	selectGridLayouts,
	selectOrderMode,
	selectSelectedChart,
	selectShowMarketModules,
	selectShowOneClickTradingOnboard,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { fetchUnmintedBoostNftForCode } from 'state/referrals/action'
import { selectIsReferralCodeValid } from 'state/referrals/selectors'
import { selectAbstractionWalletEnabled, selectWallet } from 'state/wallet/selectors'
import { PageContent } from 'styles/common'

import NetworkStatusBanner from 'components/NetworkStatusBanner'
import { FlexDivCol } from 'components/layout/flex'
import MarketFavorites from 'sections/futures/MarketFavorites'
import ChartToggle from 'sections/futures/MarketInfo/ChartToggle'
import MarketDetailsPage from 'sections/futures/MarketInfo/MarketDetailsPage'
import TradeBalance from 'sections/futures/Trade/TradeBalance'
import GlobalTradesHistoryTable from 'sections/futures/TradingHistory/GlobalTradesHistoryTable'
import UserInfo from 'sections/futures/UserInfo'
import type { ModuleType } from 'state/futures/types'

type MarketComponent = FC<MarketsDetailProps> & { getLayout: (page: ReactNode) => ReactElement }

const Market: MarketComponent = ({ marketsDetail }) => {
	const router = useRouter()
	const dispatch = useAppDispatch()
	const { width, breakpoint, greaterThanWidth } = useWindowSize()
	usePollMarketFuturesData()
	useFetchAbstractionData()
	const routerMarketAsset = router.query.asset as FuturesMarketAsset
	const setCurrentMarket = useAppSelector(selectMarketAsset)
	const showOneClickTradingOnboard = useAppSelector(selectShowOneClickTradingOnboard)
	const openModal = useAppSelector(selectShowModal)
	const showPositionModal = useAppSelector(selectShowPositionModal)
	const showConditionalOrderModal = useAppSelector(selectShowConditionalOrderModal)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const selectedMarketAsset = useAppSelector(selectMarketAsset)
	const routerReferralCode = (router.query.ref as string)?.toLowerCase()
	const isReferralCodeValid = useAppSelector(selectIsReferralCodeValid)
	const walletAddress = useAppSelector(selectWallet)

	const gridLayouts = useAppSelector(selectGridLayouts)
	const customLayout = useAppSelector(selectCustomLayout)
	const defaultLayouts = useAppSelector(selectDefaultLayouts)
	const orderMode = useAppSelector(selectOrderMode)
	const showMarketModules = useAppSelector(selectShowMarketModules)

	const selectedChart = useAppSelector(selectSelectedChart)
	const openMarketDropdownList = useAppSelector(selectOpenMarketDropdownList)

	const { ref } = useClickOutside(() => dispatch(setOpenModal(null)))
	const showBanner = useAppSelector(selectShowBanner)

	const containerRef = useRef<HTMLDivElement>(null)

	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

	const updatePosition = useCallback(() => {
		// react-grid-layout adds a class to each grid item
		const gridItem = document.querySelector('.react-grid-item.market-details')
		if (gridItem) {
			const rect = gridItem.getBoundingClientRect()
			setDropdownPosition({
				top: rect.bottom + 10,
				left: rect.left,
			})
		}
	}, [])

	useEffect(() => {
		if (openMarketDropdownList) {
			requestAnimationFrame(updatePosition)
		}
	}, [openMarketDropdownList, updatePosition])
	const abstractionWalletEnabled = useAppSelector(selectAbstractionWalletEnabled)

	const routerProviderType = useMemo(() => {
		// Legacy routing
		const type = router.query.accountType
		if (type === 'smart_margin' && SNX_V2_ENABLED) return PerpsProvider.SNX_V2_OP
		if (type === 'cross_margin' && SNX_V3_BASE_ENABLED) return PerpsProvider.SNX_V3_BASE

		// Current routing
		const provider = router.query.provider as PerpsProvider
		if (provider === PerpsProvider.SNX_V2_OP && SNX_V2_ENABLED) return PerpsProvider.SNX_V2_OP
		if (provider === PerpsProvider.SNX_V3_BASE && SNX_V3_BASE_ENABLED)
			return PerpsProvider.SNX_V3_BASE

		return DEFAULT_PERPS_PROVIDER
	}, [router.query.accountType, router.query.provider])

	useEffect(() => {
		if (router.isReady && perpsProvider !== routerProviderType) {
			dispatch(setPreferences({ selectedProvider: routerProviderType }))
		}
	}, [dispatch, perpsProvider, router.isReady, routerProviderType])

	// biome-ignore lint/correctness/useExhaustiveDependencies: Clear trade state when switching address
	useEffect(() => {
		dispatch(clearTradeInputs())
	}, [dispatch, walletAddress])

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (
			selectedMarketAsset !== routerMarketAsset &&
			routerMarketAsset &&
			MarketKeyByAsset[routerMarketAsset]
		) {
			dispatch(setMarketAsset(routerMarketAsset))
			dispatch(clearTradeInputs())
		} else {
			if (!routerMarketAsset) {
				router.replace(
					`${router.pathname}?${new URLSearchParams({
						asset: selectedMarketAsset,
						provider: routerProviderType,
					}).toString()}`
				)
			}
		}
	}, [router, setCurrentMarket, dispatch, routerMarketAsset, selectedMarketAsset])

	useLayoutEffect(() => {
		if (router.isReady && routerReferralCode) {
			dispatch(fetchUnmintedBoostNftForCode(routerReferralCode))
		}
	}, [dispatch, router.isReady, routerReferralCode])

	useLayoutEffect(() => {
		if (isReferralCodeValid) {
			dispatch(setOpenModal('referrals_mint_boost_nft'))
		}
	}, [dispatch, isReferralCodeValid])

	const onDismiss = useCallback(() => {
		dispatch(setOpenModal(null))
	}, [dispatch])

	useEffect(() => {
		dispatch(setUserInfoTableFilter(null))
		return () => {
			dispatch(setUserInfoTableFilter(null))
		}
	}, [dispatch])

	const hideShowModule = useCallback(
		(module: ModuleType) => dispatch(setMarketModules({ module, value: false })),
		[dispatch]
	)

	const getDefaultDataGrid = useCallback(
		(key: string) => {
			return gridLayouts[breakpoint]?.find((item) => item.i === key)
				? undefined
				: defaultLayouts[breakpoint].find((item) => item.i === key)
		},
		[gridLayouts, breakpoint, defaultLayouts]
	)

	const layouts = useMemo(() => {
		if (!customLayout) {
			const clonedLayouts = JSON.parse(JSON.stringify(defaultLayouts)) // Deep clone

			Object.keys(clonedLayouts).forEach((key) => {
				clonedLayouts[key] = defaultLayouts[key].map((layout) => {
					// Create a new layout object with updated 'static' property
					return { ...layout, static: true }
				})
			})
			return clonedLayouts
		}
		return gridLayouts
	}, [gridLayouts, defaultLayouts, customLayout])

	if (!width) return null
	return (
		<div>
			<MarketHead />
			<OneClickTradingOnboard isOpen={showOneClickTradingOnboard} />
			<NetworkStatusBanner />
			<DesktopOnlyView>
				<StyledPageContent>
					<ResponsiveGridLayout layouts={layouts}>
						<CustomGridItemComponent key="trade-panel" isStatic>
							<TradePanel />
						</CustomGridItemComponent>
						{showMarketModules.accountDetails && (
							<CustomGridItemComponent
								isStatic={!customLayout}
								key="account-details"
								data-grid={getDefaultDataGrid('account-details')}
								onCloseClick={() => hideShowModule('accountDetails')}
							>
								<TradeBalanceContainer>
									<TradeBalance />
								</TradeBalanceContainer>
							</CustomGridItemComponent>
						)}
						{showMarketModules.favorites && (
							<CustomGridItemComponent
								isStatic={!customLayout}
								key="favorites"
								data-grid={getDefaultDataGrid('favorites')}
								onCloseClick={() => hideShowModule('favorites')}
							>
								<MarketFavorites />
							</CustomGridItemComponent>
						)}
						{showMarketModules.marketDetails && (
							<CustomGridItemComponent
								key="market-details"
								data-grid={getDefaultDataGrid('market-details')}
								className="market-details"
								onCloseClick={() => hideShowModule('marketDetails')}
								isStatic
							>
								<MarketDetails />
								{openMarketDropdownList && (
									<StyledDialogOverlay>
										<DropdownContainer
											position={dropdownPosition}
											showBanner={showBanner}
											ref={ref}
										>
											<MarketsDropdownList />
										</DropdownContainer>
									</StyledDialogOverlay>
								)}
							</CustomGridItemComponent>
						)}
						{showMarketModules.priceChart && (
							<CustomGridItemComponent
								isStatic={!customLayout}
								key="price-charts"
								data-grid={getDefaultDataGrid('price-charts')}
								onCloseClick={() => hideShowModule('priceChart')}
							>
								<ChartContainer>
									<ChartToggle className={DRAG_HANDLE_CLASS_NAME} />
									<PositionChart display={selectedChart === 'price'} />
									<FundingChart display={selectedChart === 'funding'} />
									{selectedChart === 'market_details' && (
										<MarketDetailsPage marketsDetail={marketsDetail} display />
									)}
								</ChartContainer>
							</CustomGridItemComponent>
						)}

						{showMarketModules.history && (
							<CustomGridItemComponent
								isStatic={!customLayout}
								key="global-trades-history"
								data-grid={getDefaultDataGrid('global-trades-history')}
								ref={containerRef}
								onCloseClick={() => hideShowModule('history')}
							>
								<GlobalTradesHistoryTable
									display={showMarketModules.history}
									containerHeight={containerRef.current?.clientHeight ?? 0}
								/>
							</CustomGridItemComponent>
						)}
						{showMarketModules.userInfo && (
							<CustomGridItemComponent
								isStatic={!customLayout}
								key="user-info"
								data-grid={getDefaultDataGrid('user-info')}
								onCloseClick={() => hideShowModule('userInfo')}
							>
								<UserInfo />
							</CustomGridItemComponent>
						)}
					</ResponsiveGridLayout>
				</StyledPageContent>
			</DesktopOnlyView>
			<MobileOrTabletView>
				{greaterThanWidth('md') ? (
					<StyledPageContent>
						<TabletContainer orderMode={orderMode}>
							{orderMode === 'left' ? (
								<>
									<FlexDivCol>
										<TradePanel />
										<TradeBalanceTabletContainer>
											<TradeBalance />
										</TradeBalanceTabletContainer>
									</FlexDivCol>
									<TabletRightSection>
										<MobileTrade />
									</TabletRightSection>
								</>
							) : (
								<>
									<TabletRightSection>
										<MobileTrade />
									</TabletRightSection>
									<FlexDivCol>
										<TradePanel />
										<TradeBalanceTabletContainer>
											<TradeBalance />
										</TradeBalanceTabletContainer>
									</FlexDivCol>
								</>
							)}
						</TabletContainer>
					</StyledPageContent>
				) : (
					<MobileTrade />
				)}
			</MobileOrTabletView>
			{(showPositionModal?.type === 'smart_margin_close_position' ||
				showPositionModal?.type === 'cross_margin_close_position') && <ClosePositionModal />}
			{showPositionModal?.type === 'futures_edit_stop_loss_take_profit' && (
				<EditStopLossAndTakeProfitModal />
			)}
			{showPositionModal?.type === 'futures_edit_position_size' && <EditPositionSizeModal />}
			{showPositionModal?.type === 'futures_edit_position_margin' && <EditPositionMarginModal />}
			{showConditionalOrderModal?.type === 'futures_edit_conditional_order' && (
				<EditConditionalOrderModal />
			)}
			{showConditionalOrderModal?.type === 'futures_edit_stop_loss_take_profit' && (
				<EditStopLossAndTakeProfitModal />
			)}
			{openModal === 'futures_deposit_withdraw_cross_margin' && (
				<ManageCrossMarginModal onDismiss={onDismiss} />
			)}
			{openModal === 'futures_deposit_withdraw_isolated_margin' && (
				<ManageIsolatedMarginModal onDismiss={onDismiss} />
			)}
			{openModal === 'futures_confirm_isolated_margin_trade' && (
				<TradeConfirmationModalSmartMargin />
			)}
			{openModal === 'futures_confirm_cross_margin_trade' && <CrossMarginOrderConfirmationModal />}
			{openModal === 'referrals_mint_boost_nft' && routerReferralCode && isReferralCodeValid && (
				<BaseReferralModal onDismiss={onDismiss} referralCode={routerReferralCode} />
			)}
			{openModal === 'table_time_filter' && <TableFiltersModal />}
			{openModal === 'acknowledgement_terms' && <AcknowledgementModal onDismiss={onDismiss} />}
			{openModal === 'futures_create_account' &&
				(abstractionWalletEnabled ? (
					<AbstractionAccountSetupModal onDismiss={onDismiss} />
				) : (
					<CrossMarginOnboarding onDismiss={onDismiss} />
				))}
			{openModal === 'futures_deposit_funds' && <DepositFundsModal onDismiss={onDismiss} />}
		</div>
	)
}

Market.getLayout = (page) => <AppLayout>{page}</AppLayout>

export default Market

export async function getStaticProps() {
	const filePath = path.join(process.cwd(), 'public', 'markets-data.json')
	const jsonData = await fsPromises.readFile(filePath, 'utf8')

	return {
		props: {
			marketsDetail: JSON.parse(jsonData),
		},
	}
}

const StyledPageContent = styled(PageContent)`
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
`

const StyledDialogOverlay = styled(DialogOverlay)`
	z-index: 10;
	background: rgba(0, 0, 0, 0.5);
`

const DropdownContainer = styled.div<{
	position: { top: number; left: number }
	showBanner: boolean
}>`
	position: absolute;
	z-index: 100;
	top: ${(props) => props.position.top}px;
	left: ${(props) => props.position.left}px;
`

const ChartContainer = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
`

const TabletContainer = styled.div<{ orderMode: 'left' | 'right' }>`
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	display: grid;
	grid-template-columns: ${(props) => (props.orderMode === 'left' ? `${TRADE_PANEL_WIDTH_MD}px 1fr` : `1fr ${TRADE_PANEL_WIDTH_MD}px`)} ;
	height: 100%;
`

const TabletRightSection = styled.div`
	overflow-y: scroll;
	height: 100%;
	display: flex;
	flex-direction: column;
`

const TradeBalanceContainer = styled.div`
	z-index: 10;
	width: 100%;
	height: 100%;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.containers.secondary.background};
`

const TradeBalanceTabletContainer = styled(TradeBalanceContainer)`
	height: auto;
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	border-right: ${(props) => props.theme.colors.selectedTheme.border};
`
